import React, { FC, memo, useEffect, useState } from 'react';
import { down, up } from 'styled-breakpoints';
import styled from 'styled-components';
import SimpleLinkButton from '../../../../components/Buttons/SimpleLinkButton';
import { GIFT_PURCHASE_IMPACT_ID } from '../../../../constants/impact';
import { verifyEmail } from '../../../../services/auth';
import {
  logCustomPageView,
  logFBEvent,
  logGAEvent,
  logImpactEvent,
  logLegacyGtagEvent,
  pinterestTrack,
  tiktokLogPurchase,
} from '../../../../utils/analytics';
import useKlaviyo from '../../../sale-event/hooks/useKlaviyo';
import { KlaviyoEvent, PinterestEvent, TrackingEventAction, TrackingEventCategory } from '../../../sale-event/types';
import { GiftThankYouState } from '../../types';
import CreateAccountForm from '../CreateAccountForm';

export const Title = styled.span`
  font-size: 30px;
  font-weight: 600;

  ${down('sm')} {
    font-size: 21px;
  }
`;

export const SubTitle1 = styled.span`
  font-size: 24px;
  font-weight: 500;
  ${down('sm')} {
    font-size: 14px;
  }
`;

export const SubTitle2 = styled.span`
  font-size: 18px;
  font-weight: 500;
  line-height: 18px;

  ${down('sm')} {
    font-size: 14px;
    font-weight: 600;
  }
`;

const Wrapper = styled.div`
  ${up('md')} {
    min-height: 47vh;
  }
`;

const CreateAccountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .space-y {
    margin: 5vh 0;
  }
`;

const CardContainer = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
  width: 100%;
  flex-wrap: wrap;
  height: auto;
  position: relative;
  padding: 0;
  margin: 0;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  padding: 40px 45px;
  margin-bottom: 1em;
  border-radius: 0.5em;
  border-style: solid;
  border-width: thin;
  border-color: #eef1f5;
  background: #fff;
  font-size: 16px;
  font-weight: 700;
  line-height: 50px;
  color: #272727;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  text-align: center;

  ${down('sm')} {
    padding: 10px;
    font-size: 12px;
    line-height: 25px;
  }

  ${SubTitle2} {
    padding: 20px 0;
  }
`;

type Props = {
  onBack: () => void;
  recipientName: string;
  recipientEmail: string;
  state: GiftThankYouState;
  updateState: (arg0: GiftThankYouState) => void;
};

const SuccessfulGift: FC<Props> = ({ state, onBack, updateState, recipientName, recipientEmail }) => {
  const [isUserWithPassword, setIsUserWithPassword] = useState(true);
  const { trackProperties } = useKlaviyo(true, {
    email: state.senderEmail,
    first_name: state.user.first_name,
    last_name: state.user.last_name,
  });

  useEffect(() => {
    async function checkUser() {
      try {
        const response = await verifyEmail(state.user.email);
        setIsUserWithPassword(response.has_password_or_social_auth);
      } catch (e) {}
    }
    if (state.user) {
      checkUser().then();
    }
  }, [state.user]);

  // events tracking
  useEffect(() => {
    logCustomPageView('gift-card/thank-you');
    trackProperties(KlaviyoEvent.GIFT_CARD_PAID, { amount: state.amount });

    logGAEvent(TrackingEventCategory.GIFT_CARD, TrackingEventAction.PURCHASE, state.amount);
    logGAEvent(TrackingEventCategory.TRACK, TrackingEventAction.PURCHASE, state.amount);

    logFBEvent(TrackingEventCategory.TRACK, TrackingEventAction.PURCHASE, { currency: 'USD', value: state.amount });
    logFBEvent(TrackingEventCategory.TRACK_CUSTOM, TrackingEventAction.GIFT_CARD_PURCHASE, {
      currency: 'USD',
      value: state.amount,
    });
    tiktokLogPurchase(TrackingEventCategory.GIFT_CARD, state.amount);

    logLegacyGtagEvent(TrackingEventCategory.GIFT_CARD, TrackingEventAction.CONVERSION, {
      send_to: 'AW-776690019/lQtHCJWUvqEBEOOyrfIC',
      transaction_id: state.orderId,
      value: state.amount,
      currency: 'USD',
    });

    logImpactEvent(GIFT_PURCHASE_IMPACT_ID, {
      currencyCode: 'USD',
      customerId: state.user.id.toString(),
      customerEmail: state.user.email_sha1,
      customerStatus: state.user.stripe_payment_source_last4 ? 'Returning' : 'New',
      orderId: state.orderId,
      orderDiscount: state.discount,
      orderPromoCode: state.coupon,
      items: [
        {
          name: 'Gift card',
          sku: 'dopple-gift-card',
          category: 'Gift card',
          quantity: 1,
          price: state.cardPrice,
        },
      ],
    });

    pinterestTrack(PinterestEvent.CHECKOUT, {
      value: state.amount,
      currency: 'USD',
      order_id: state.orderId,
      promo_code: state.coupon,
      line_items: [
        {
          product_name: 'Gift card',
          product_id: 'gift-card',
          product_price: state.amount,
          product_quantity: 1,
          product_category: 'gift-card',
        },
      ],
    });
  }, []);

  return (
    <Wrapper>
      <CardContainer>
        <Card>
          <Title>Thanks for your gift!</Title>
          <SubTitle2>
            {recipientName} will receive an email sent to <strong>{recipientEmail}</strong> with your gift card shortly!
          </SubTitle2>
          <SimpleLinkButton href="https://thedopple.com/gifts">SEND ANOTHER GIFT?</SimpleLinkButton>
        </Card>
      </CardContainer>

      {!isUserWithPassword && (
        <CreateAccountWrapper>
          <hr className="width-full opacity-50 space-y" />
          <CreateAccountForm state={state} updateState={updateState} />
        </CreateAccountWrapper>
      )}
    </Wrapper>
  );
};

export default memo(SuccessfulGift);
